"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Search, MessageSquare } from "lucide-react";
import { Button } from "../../../components/ui/button";
import Link from "next/link";

const categories = [
  { id: "general", name: "General" },
  { id: "booking", name: "Booking & Payments" },
  { id: "experience", name: "The Experience" },
  { id: "logistics", name: "Logistics & Safety" }
];

const faqs = [
  {
    category: "general",
    question: "What exactly is Outcome Outing?",
    answer: "Outcome Outing is a curated travel platform that designs mentor-led, growth-focused trips for entrepreneurs, creators, and students. We combine travel with intentional networking and learning outcomes."
  },
  {
    category: "general",
    question: "Who can apply for these trips?",
    answer: "Anyone who has a clear intent for growth. While each trip has a target audience (e.g., founders, solo-travelers, career seekers), we prioritize applicants based on their story and goals."
  },
  {
    category: "booking",
    question: "How do I pay for the trip?",
    answer: "Once you are shortlisted and receive an invitation, you can pay via UPI, Credit/Debit cards, or Net Banking through our secure payment gateway."
  },
  {
    category: "booking",
    question: "What is your cancellation policy?",
    answer: "We offer full refunds if cancelled 30 days before the trip. For cancellations within 15-30 days, we credit 50% for future trips. Please check our detailed Cancellation Policy page for more info."
  },
  {
    category: "experience",
    question: "Is there a lot of sightseeing?",
    answer: "We focus on 'slow travel'. There is sightseeing, but it's balanced with workshops, bonfire talks, and downtime for reflection. It's not a generic hectic tour."
  },
  {
    category: "experience",
    question: "Will I have to share a room?",
    answer: "Most of our stays are in premium villas/boutique hotels on a twin-sharing basis with people of the same gender. Private rooms can be requested at an additional cost."
  },
  {
    category: "logistics",
    question: "Is travel to the destination included?",
    answer: "Trips usually start from the designated meeting point at the destination. Airfare or train travel to that point is not included unless specified otherwise."
  },
  {
    category: "logistics",
    question: "Are your trips safe for solo female travelers?",
    answer: "Absolutely. Safety is our top priority. Our coordinators are trained, and we ensure our groups and stays are secure and respectful at all times."
  }
];

const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-0 py-6">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center w-full text-left group"
      >
        <h3 className={`text-lg md:text-xl font-bold transition-colors ${isOpen ? 'text-[#FFAE00]' : 'text-[#063258] group-hover:text-[#FFAE00]'}`}>
          {question}
        </h3>
        <div className={`shrink-0 ml-4 p-1 rounded-full ${isOpen ? 'bg-[#FFAE00] text-white' : 'bg-gray-100 text-gray-500'}`}>
          {isOpen ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="mt-4 text-gray-600 leading-relaxed text-lg">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FaqPage = () => {
  const [activeCategory, setActiveCategory] = useState("general");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFaqs = faqs.filter(faq => 
    (faq.category === activeCategory || searchQuery !== "") &&
    (faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
     faq.answer.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="bg-white min-h-screen font-jakarta">
      {/* Header */}
      <section className="bg-white py-20 px-6 border-b border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bricolage font-bold text-[#063258] mb-8">
            Frequently Asked Questions
          </h1>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input 
              type="text" 
              placeholder="Search for answers..."
              className="w-full pl-12 pr-6 py-5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#FFAE00] focus:border-transparent outline-none text-lg transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 px-6 max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-12">
        {/* Sidebar Categories */}
        <div className="lg:col-span-1 space-y-2">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => {
                setActiveCategory(cat.id);
                setSearchQuery("");
              }}
              className={`w-full text-left px-6 py-4 rounded-xl font-bold transition-all ${
                activeCategory === cat.id && searchQuery === ""
                  ? 'bg-[#063258] text-white shadow-lg'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-xl shadow-gray-100/50">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, idx) => (
                <FaqItem key={idx} question={faq.question} answer={faq.answer} />
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No matching questions found.</p>
                <button 
                  onClick={() => setSearchQuery("")}
                  className="text-[#FFAE00] font-bold mt-2"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Still need help? */}
      <section className="py-20 px-6 bg-[#FFAE00]/10 text-center">
        <h2 className="text-2xl md:text-3xl font-bricolage font-bold text-[#063258] mb-6">
          Didn&apos;t find what you were looking for?
        </h2>
        <p className="text-gray-600 mb-8 max-w-xl mx-auto text-lg">
          Our team is just a message away. We usually respond within minutes.
        </p>
        <Link href="https://wa.me/917982579691" target="_blank">
          <Button className="bg-[#063258] hover:bg-[#0D0D0D] text-white px-10 py-7 rounded-2xl text-xl font-bold flex items-center gap-3 mx-auto transition-all shadow-lg">
            <MessageSquare className="w-6 h-6" /> Chat on WhatsApp
          </Button>
        </Link>
      </section>
    </div>
  );
};

export default FaqPage;
